import { ContentBox } from "@msuite/ui/content-box";
import { useTranslation } from "@msuite/ui/localization";
import { Tabs } from "@msuite/ui/tabs";
import { Fragment } from "react";
import { OrganizationCard } from "@/components/cards";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";
import { OrganizationSettingsAbsences } from "./organization-settings-absences";
import { OrganizationSettingsAlerts } from "./organization-settings-alerts";
import { OrganizationSettingsContacts } from "./organization-settings-contacts";
import { OrganizationSettingsGeneral } from "./organization-settings-general";
import { OrganizationSettingsIntegrations } from "./organization-settings-integrations";
import { OrganizationSettingsInvoices } from "./organization-settings-invoices";
import { OrganizationSettingsMembers } from "./organization-settings-members";
import { OrganizationSettingsRoles } from "./organization-settings-roles";
import { OrganizationSettingsTeams } from "./organization-settings-teams";
import { OrganizationSettingsTickets } from "./organization-settings-tickets";
import { OrganizationSettingsWorksites } from "./organization-settings-worksites";
import { OrganizationSettingsWorkspaces } from "./organization-settings-workspaces";

export function OrganizationSettings() {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const { canManageRoles, citadel } = useCitadel();

	/** Computed */
	// The panels below read and write the organisation's own configuration — its details,
	// workspaces, worksite acknowledgements, absence rules, ticket and contact types, alerts —
	// which the server gates on the `Organization` subject. A caller without the rule would get
	// the tab, the form, and a refusal from every request in it, so the tab is dropped instead.
	const canReadOrganization = citadel?.can("read", "Organization") === true;
	const canReadTeams = citadel?.can("read", "Team") === true;
	// The members tab is the same roster the people page shows, contact columns and all, so it
	// follows the same rule. Who the colleagues are stays readable throughout the product — the
	// members endpoint is ungated and every picker is made of it — but the surface that lists
	// them with their addresses is `read User`.
	const canReadUsers = citadel?.can("read", "User") === true;
	// The integrations tab reads whether Personio and Webfleet are set up, which the server gates
	// on `read Integration`. Without the rule the tab renders two cards whose first request is
	// refused, so it is dropped like the others.
	const canReadIntegrations = citadel?.can("read", "Integration") === true;

	/** Render */
	return (
		<Fragment>
			<ContentBox>
				<div className="card">
					<OrganizationCard orgId={organization?.id} />
				</div>
				<Tabs
					withHeader
					groups={[
						{
							name: "settings",
							panels: [
								canReadOrganization
									? {
											label: t("general.general"),
											panel: () => <OrganizationSettingsGeneral />,
											errors: null,
											cyId: "general-tab",
										}
									: null,
								canReadOrganization
									? {
											label: t("organization.workspaces"),
											panel: () => <OrganizationSettingsWorkspaces />,
											errors: null,
											cyId: "workspaces-tab",
										}
									: null,
								canReadOrganization
									? {
											label: t("organization.worksites"),
											panel: () => <OrganizationSettingsWorksites />,
											errors: null,
											cyId: "worksites-tab",
										}
									: null,
								canReadUsers
									? {
											label: t("organization.members"),
											panel: () => <OrganizationSettingsMembers />,
											errors: null,
											cyId: "members-tab",
										}
									: null,
								canReadTeams
									? {
											label: t("organization.teams"),
											panel: () => <OrganizationSettingsTeams />,
											errors: null,
											cyId: "teams-tab",
										}
									: null,
								// Every write behind this tab requires owner-or-admin on the server, so a
								// reader without it would get the table, the create button, and a refusal
								// from every action in it. The tab is dropped instead — `Tabs` takes a null
								// panel for exactly this.
								canManageRoles
									? {
											label: t("roles.roles"),
											panel: () => <OrganizationSettingsRoles />,
											errors: null,
											cyId: "roles-tab",
										}
									: null,
								canReadOrganization
									? {
											label: t("absences.absences"),
											panel: () => <OrganizationSettingsAbsences />,
											errors: null,
											cyId: "absences-tab",
										}
									: null,
								canReadOrganization
									? {
											label: t("projects.tickets"),
											panel: () => <OrganizationSettingsTickets />,
											errors: null,
											cyId: "tickets-tab",
										}
									: null,
								canReadIntegrations
									? {
											label: t("integrations.integrations"),
											panel: () => <OrganizationSettingsIntegrations />,
											errors: null,
											cyId: "integrations-tab",
										}
									: null,
								// The invoices tab manages the mailboxes polled for incoming invoices — credentials
								// this server holds for an external account, which the server gates on
								// `Integration` like Personio and Webfleet. Same rule, same reason to drop the tab.
								canReadIntegrations
									? {
											label: t("invoices.invoices"),
											panel: () => <OrganizationSettingsInvoices />,
											errors: null,
											cyId: "invoices-tab",
										}
									: null,
								canReadOrganization
									? {
											label: t("contacts.contacts"),
											panel: () => <OrganizationSettingsContacts />,
											errors: null,
											cyId: "contacts-tab",
										}
									: null,
								canReadOrganization
									? {
											label: t("dashboard.alerts.alerts"),
											panel: () => <OrganizationSettingsAlerts />,
											errors: null,
											cyId: "alerts-tab",
										}
									: null,
							],
						},
					]}
				/>
			</ContentBox>
		</Fragment>
	);
}
